import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { formatUGX } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "240px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #1a237e 0%, #283593 60%, #3949ab 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },
  heroActions: { position: "absolute", top: "40px", right: "40px", zIndex: 3, display: "flex", gap: "12px" },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "220px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "18px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "11px", color: "#64748b", margin: "2px 0 0", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px" },
  statSub: { fontSize: "11px", color: "#94a3b8", margin: "2px 0 0" },

  // Toolbar
  toolbarWrap: {
    background: "#fff",
    padding: "16px 24px",
    borderRadius: "16px 16px 0 0",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexWrap: "wrap",
    gap: "16px"
  },
  filterGroup: { display: "flex", alignItems: "center", gap: "12px" },
  filterSelect: { 
    padding: "8px 12px", 
    borderRadius: "8px", 
    border: "1px solid #e2e8f0", 
    fontSize: "13px", 
    color: "#475569",
    outline: "none",
    background: "#f8fafc",
    "&:focus": { borderColor: "#3f51b5", background: "#fff" }
  },
  resultsCount: { fontSize: "13px", color: "#64748b", fontWeight: 500 },

  tableContainer: {
    background: "#fff",
    borderRadius: "0 0 16px 16px",
    overflow: "hidden",
    boxShadow: "0 4px 6px -1px rgba(0,0,0,0.05)"
  },
  formControl: { width: "100%", marginTop: "16px" },
  errorMsg: { color: "#f44336", fontSize: "13px", background: "#fee2e2", padding: "10px", borderRadius: "8px", marginBottom: "16px" },
  total: { fontWeight: "bold", color: "#1a237e", fontSize: "15px" },
  amountCell: { fontWeight: "700", color: "#1e293b" }
};

const BLANK = { programmeId: "", semesterId: "", itemName: "", amount: "" };

class FeeStructure extends Component {
  state = {
    feeItems: [], programmes: [], semesters: [],
    loading: true, saving: false,
    open: false, form: { ...BLANK }, error: null,
    filterProgramme: "", filterSemester: ""
  };

  componentDidMount() { this.fetchAll(); }

  fetchAll = async () => {
    try {
      const [fRes, pRes, sRes] = await Promise.all([
        api.get("/fees/structure?limit=200").catch(() => ({ data: { data: [] } })),
        api.get("/programmes?limit=100"),
        api.get("/semesters?limit=100")
      ]);
      const fd = fRes.data.data || fRes.data;
      const pd = pRes.data.data || pRes.data;
      const sd = sRes.data.data || sRes.data;
      this.setState({
        feeItems: Array.isArray(fd) ? fd : fd.data || [],
        programmes: Array.isArray(pd) ? pd : pd.data || [],
        semesters: Array.isArray(sd) ? sd : sd.data || [],
        loading: false
      });
    } catch (e) { this.setState({ loading: false }); }
  };

  openNew = () => this.setState({ open: true, form: { ...BLANK }, error: null });
  handleClose = () => this.setState({ open: false });
  handleChange = field => e => { const v = e.target.value; this.setState(prev => ({ form: { ...prev.form, [field]: v } })); };

  handleSave = async () => {
    const { form } = this.state;
    if (!form.programmeId || !form.itemName || !form.amount) { this.setState({ error: "Programme, item name, and amount are required." }); return; }
    this.setState({ saving: true });
    try {
      await api.post("/fees/structure", { ...form, amount: parseInt(form.amount, 10) });
      this.setState({ open: false, saving: false });
      this.fetchAll();
    } catch (e) { this.setState({ error: e.response?.data?.message || "Failed.", saving: false }); }
  };

  render() {
    const { classes } = this.props;
    const { feeItems, programmes, semesters, loading, open, form, saving, error, filterProgramme, filterSemester } = this.state;

    const filtered = feeItems.filter(f =>
      (!filterProgramme || f.programmeId === filterProgramme) &&
      (!filterSemester || f.semesterId === filterSemester)
    );
    const total = filtered.reduce((sum, f) => sum + (f.amount || 0), 0);
    const uniqueProgrammes = new Set(filtered.map(f => f.programmeId)).size;

    const columns = [
      {
        id: "item",
        header: "Fee Item",
        accessorFn: f => f.itemName,
        cell: info => <span style={{ fontWeight: "700", color: "#1e293b" }}>{info.getValue()}</span>
      },
      { id: "programme", header: "Programme", accessorFn: f => f.programme?.name || "—" },
      { id: "semester", header: "Semester", accessorFn: f => f.semester?.name || "All" },
      {
        id: "amount",
        header: "Amount (UGX)",
        accessorFn: f => f.amount || 0,
        meta: { align: "right" },
        cell: info => <span className={classes.amountCell}>{formatUGX(info.getValue())}</span>
      }
    ];

    return (
      <GridContainer>
        <GridItem xs={12}>
          <div className={classes.heroWrap}>
            <div className={classes.heroOverlay} />
            <div className={classes.heroContent}>
              <h1 className={classes.heroTitle}>Fee Structure</h1>
              <p className={classes.heroSub}>
                Institutional fee configuration by programme and semester
              </p>
            </div>
            <div className={classes.heroActions}>
              <Button color="white" onClick={this.openNew} round style={{ fontWeight: 700, color: "#1a237e" }}>
                <Icon style={{ marginRight: "8px" }}>add_circle</Icon> New Fee Item
              </Button>
            </div>
          </div>

          <div className={classes.kpiRow}>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "rgba(26, 35, 126, 0.1)", color: "#1a237e" }}>
                <Icon>receipt_long</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{filtered.length}</p>
                <p className={classes.statLabel}>Fee Items</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "rgba(76, 175, 80, 0.1)", color: "#2e7d32" }}>
                <Icon>payments</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>UGX {total.toLocaleString()}</p>
                <p className={classes.statLabel}>Total Amount</p>
              </div>
            </div>
            <div className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: "rgba(255, 152, 0, 0.1)", color: "#ef6c00" }}>
                <Icon>school</Icon>
              </div>
              <div>
                <p className={classes.statNumber}>{uniqueProgrammes}</p>
                <p className={classes.statLabel}>Programmes Covered</p>
              </div>
            </div>
          </div>

          <div className={classes.tableContainer}>
            <div className={classes.toolbarWrap}>
              <div className={classes.filterGroup}>
                <select className={classes.filterSelect}
                  value={filterProgramme} onChange={e => this.setState({ filterProgramme: e.target.value })}>
                  <option value="">All Programmes</option>
                  {programmes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
                <select className={classes.filterSelect}
                  value={filterSemester} onChange={e => this.setState({ filterSemester: e.target.value })}>
                  <option value="">All Semesters</option>
                  {semesters.map(s => <option key={s.id} value={s.id}>{s.name} — {s.academicYear?.name || ""}</option>)}
                </select>
              </div>
              <div className={classes.resultsCount}>
                Showing <strong>{filtered.length}</strong> fee items
              </div>
            </div>

            <DataTable
              columns={columns}
              data={filtered}
              isLoading={loading}
              emptyState={
                <div>
                  <Icon style={{ fontSize: "48px", color: "#e2e8f0" }}>receipt</Icon>
                  <p style={{ color: "#94a3b8", marginTop: "16px", fontWeight: "500" }}>No fee items found for the selected filters.</p>
                </div>
              }
              footer={filtered.length > 0 ? { label: "TOTAL", values: { amount: <span className={classes.total}>{formatUGX(total)}</span> } } : undefined}
            />
          </div>
        </GridItem>

        <Dialog open={open} onClose={this.handleClose} maxWidth="sm" fullWidth>
          <DialogHeader title="Add Fee Item" />
          <DialogContent>
            {error && <p className={classes.errorMsg}>{error}</p>}
            <FormControl className={classes.formControl}>
              <InputLabel>Programme *</InputLabel>
              <Select value={form.programmeId} onChange={this.handleChange("programmeId")}>
                <MenuItem value="">— Select —</MenuItem>
                {programmes.map(p => <MenuItem key={p.id} value={p.id}>{p.name}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl className={classes.formControl}>
              <InputLabel>Semester (optional)</InputLabel>
              <Select value={form.semesterId} onChange={this.handleChange("semesterId")}>
                <MenuItem value="">All Semesters</MenuItem>
                {semesters.map(s => <MenuItem key={s.id} value={s.id}>{s.name} — {s.academicYear?.name || ""}</MenuItem>)}
              </Select>
            </FormControl>
            <CustomInput labelText="Fee Item Name *" id="itemName" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.itemName, onChange: this.handleChange("itemName"), placeholder: "e.g. Tuition Fee, Functional Fee..." }} />
            <CustomInput labelText="Amount (UGX) *" id="famount" formControlProps={{ fullWidth: true }}
              inputProps={{ value: form.amount, onChange: this.handleChange("amount"), type: "number", placeholder: "Amount in Uganda Shillings" }} />
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={this.handleClose} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save"}
            </Button>
          </DialogFooter>
        </Dialog>
      </GridContainer>
    );
  }
}

FeeStructure.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(FeeStructure));
